import moment from "@/date";
import type { Moment } from "moment";
import { ReportType } from "@/modules/reports/types";
import type { PublicHolidayRangeInterface, ReportInterface } from "@/modules/reports/types";
import type { WeekScheduleUserInterface } from "../types";

export const WORKDAYS_PER_WEEK = 5;
export const WORKDAY_WIDTH_PERCENT = 100 / WORKDAYS_PER_WEEK;

export type TimelineStatus =
  | "assigned"
  | "assigned-to-available"
  | "available-to-assigned"
  | "unavailable-to-available"
  | "available"
  | "unavailable";

export interface TimelineAbsence {
  start_date?: string;
  end_date?: string;
}

export interface TimelineAssignment {
  start_date?: string | null;
  end_date?: string | null;
}

export const getEmploymentStartDate = (user: WeekScheduleUserInterface): Moment | undefined => {
  if (!user.employment_start_date) return undefined;
  return moment(user.employment_start_date).startOf("day");
};

export const getAvailabilityDate = (user: WeekScheduleUserInterface): Moment | undefined => {
  const endDate = user.main_role?.end_date;
  if (endDate) {
    let increment = 1;
    if (moment(endDate).day() === 5) {
      increment = 3; // available date is next Monday if end date is Friday
    } else if (moment(endDate).day() === 6) {
      increment = 2; // available date is next Monday if end date is Saturday
    }
    return moment(endDate).add(increment, "day").startOf("day");
  }

  return user.available_from ? moment(user.available_from).startOf("day") : undefined;
};

export const getAssignmentStartDate = (user: WeekScheduleUserInterface): Moment | undefined => {
  const startDate = user.main_role?.start_date;
  if (startDate) {
    let increment = 0; // start date is next Monday if start date is on a weekend
    if (moment(startDate).day() === 6) {
      increment = 2;
    } else if (moment(startDate).day() === 0) {
      // Sunday is index 0
      increment = 1;
    }
    return moment(startDate).add(increment, "day").startOf("day");
  }

  return undefined;
};

export const isTransitionStatus = (status: TimelineStatus): boolean =>
  status === "assigned-to-available" ||
  status === "available-to-assigned" ||
  status === "unavailable-to-available";

const startsAfterPeriodEndingMidweek = (periodStarts: Moment, weekStart: Moment): boolean => {
  const previousPeriodEnd = periodStarts.clone().subtract(1, "day");
  return (
    periodStarts.isAfter(weekStart, "day") &&
    previousPeriodEnd.isoWeekday() <= WORKDAYS_PER_WEEK - 1
  );
};

export const getTimelineStatus = (
  user: WeekScheduleUserInterface,
  week: Moment
): TimelineStatus => {
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = week.clone().endOf("isoWeek");
  const employmentStarts = getEmploymentStartDate(user);
  const availableFrom = getAvailabilityDate(user);
  const assignmentStarts = getAssignmentStartDate(user);

  if (employmentStarts?.isAfter(weekEnd, "day")) return "unavailable";
  if (employmentStarts && startsAfterPeriodEndingMidweek(employmentStarts, weekStart)) {
    return "unavailable-to-available";
  }

  if (!user.main_role) {
    return !availableFrom || availableFrom.isSameOrBefore(weekStart, "day")
      ? "available"
      : "unavailable";
  }

  if (assignmentStarts?.isAfter(weekEnd, "day")) return "available";
  if (assignmentStarts && startsAfterPeriodEndingMidweek(assignmentStarts, weekStart)) {
    return "available-to-assigned";
  }
  if (!availableFrom || availableFrom.isAfter(weekEnd, "day")) return "assigned";
  if (startsAfterPeriodEndingMidweek(availableFrom, weekStart)) return "assigned-to-available";
  return "available";
};

export const hasTimelineSegmentEndedBeforeDate = (
  user: WeekScheduleUserInterface,
  week: Moment,
  currentDate: Moment = moment()
): boolean => {
  const status = getTimelineStatus(user, week);
  const today = currentDate.clone().startOf("day");
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = week.clone().endOf("isoWeek");
  const employmentStarts = getEmploymentStartDate(user);
  const assignmentStarts = getAssignmentStartDate(user);

  if (status === "unavailable") {
    const unavailableUntil = employmentStarts?.isAfter(weekEnd, "day")
      ? employmentStarts
      : getAvailabilityDate(user);
    return unavailableUntil?.isSameOrBefore(today, "day") === true;
  }

  if (status === "unavailable-to-available" || status === "available") {
    const availablePeriodStarts =
      status === "unavailable-to-available" ? employmentStarts : weekStart;
    return (
      !!availablePeriodStarts &&
      assignmentStarts?.isAfter(availablePeriodStarts, "day") === true &&
      assignmentStarts.isSameOrBefore(today, "day")
    );
  }

  if (status === "available-to-assigned" || status === "assigned") {
    const assignmentEnds = user.main_role?.end_date
      ? moment(user.main_role.end_date).startOf("day")
      : undefined;
    return assignmentEnds?.isBefore(today, "day") === true;
  }

  return false;
};

export const getWeekdayPosition = (date: Moment, week: Moment): number => {
  const dayOffset = date.clone().startOf("day").diff(week.clone().startOf("isoWeek"), "days");
  return Math.min(Math.max(dayOffset, 0), WORKDAYS_PER_WEEK) * WORKDAY_WIDTH_PERCENT;
};

export const getCurrentDayPosition = (week: Moment, currentDate: Moment = moment()): number =>
  Math.min(getWeekdayPosition(currentDate, week) + WORKDAY_WIDTH_PERCENT / 2, 100);

export const isWaitingToStart = (
  user: WeekScheduleUserInterface,
  currentDate: Moment = moment()
): boolean => getEmploymentStartDate(user)?.isAfter(currentDate, "day") === true;

export const isUserAvailable = (
  user: WeekScheduleUserInterface,
  currentDate: Moment = moment()
): boolean => {
  if (isWaitingToStart(user, currentDate)) return false;

  const status = getTimelineStatus(user, currentDate);
  const assignmentStart = getAssignmentStartDate(user);
  const hasLaterAssignment =
    status === "available" &&
    assignmentStart?.isAfter(currentDate.clone().add(2, "week"), "day") === true;
  return !user.main_role || hasLaterAssignment;
};

export const hasCurrentAssignment = (
  user: WeekScheduleUserInterface,
  currentDate: Moment = moment()
): boolean => !!user.main_role && !isUserAvailable(user, currentDate);

const getAbsenceRange = (absence: TimelineAbsence): [Moment, Moment] | undefined => {
  if (!absence.start_date) return undefined;

  const start = moment(absence.start_date).startOf("day");
  const end = moment(absence.end_date || absence.start_date).endOf("day");
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return undefined;

  return [start, end];
};

export const absenceOverlapsWorkWeek = (absence: TimelineAbsence, week: Moment): boolean => {
  const range = getAbsenceRange(absence);
  if (!range) return false;

  const [start, end] = range;
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = weekStart
    .clone()
    .add(WORKDAYS_PER_WEEK - 1, "days")
    .endOf("day");
  return !end.isBefore(weekStart) && !start.isAfter(weekEnd);
};

export const isAbsenceWithinContinuingAssignment = (
  absence: TimelineAbsence,
  assignment?: TimelineAssignment | null
): boolean => {
  const absenceRange = getAbsenceRange(absence);
  if (!absenceRange || !assignment?.start_date) return false;

  const [absenceStart, absenceEnd] = absenceRange;
  const assignmentStart = moment(assignment.start_date).startOf("day");
  if (!assignmentStart.isValid() || assignmentStart.isAfter(absenceStart)) return false;
  if (!assignment.end_date) return true;

  const assignmentEnd = moment(assignment.end_date).endOf("day");
  return assignmentEnd.isValid() && assignmentEnd.isAfter(absenceEnd);
};

export const getAbsenceWeekStyle = (
  absence: TimelineAbsence,
  week: Moment
): Record<string, string> => {
  const range = getAbsenceRange(absence);
  if (!range) return {};

  const [start, end] = range;
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = weekStart
    .clone()
    .add(WORKDAYS_PER_WEEK - 1, "days")
    .endOf("day");
  const visibleStart = start.isAfter(weekStart) ? start : weekStart;
  const visibleEnd = end.isBefore(weekEnd) ? end : weekEnd;
  const startOffset = visibleStart.diff(weekStart, "days");
  const endOffset = visibleEnd.clone().startOf("day").diff(weekStart, "days");

  return {
    left: `${startOffset * WORKDAY_WIDTH_PERCENT}%`,
    right: `${(WORKDAYS_PER_WEEK - 1 - endOffset) * WORKDAY_WIDTH_PERCENT}%`,
  };
};

const getNextWorkday = (date: Moment): Moment => {
  const nextWorkday = date.clone().add(1, "day").startOf("day");
  while (nextWorkday.isoWeekday() > WORKDAYS_PER_WEEK) {
    nextWorkday.add(1, "day");
  }
  return nextWorkday;
};

export const mergeConsecutiveLeaveReports = (
  reports: Array<ReportInterface>
): Array<ReportInterface> => {
  const isMergeableLeaveReport = (report: ReportInterface): boolean =>
    report.type === ReportType.ABSENCE &&
    !!report.start_date &&
    moment(report.start_date).isValid();
  const leaveReports = reports
    .filter(isMergeableLeaveReport)
    .slice()
    .sort((first, second) => moment(first.start_date).diff(moment(second.start_date)));
  const mergedLeaveReports: Array<ReportInterface> = [];

  for (const report of leaveReports) {
    const previous = mergedLeaveReports[mergedLeaveReports.length - 1];
    if (!previous) {
      mergedLeaveReports.push({ ...report } as ReportInterface);
      continue;
    }

    const previousEnd = moment(previous.end_date || previous.start_date).endOf("day");
    const reportStart = moment(report.start_date).startOf("day");
    const reportEnd = moment(report.end_date || report.start_date).endOf("day");
    const continuesPreviousPeriod = reportStart.isSameOrBefore(getNextWorkday(previousEnd), "day");

    if (!continuesPreviousPeriod) {
      mergedLeaveReports.push({ ...report } as ReportInterface);
      continue;
    }

    if (reportEnd.isAfter(previousEnd)) {
      previous.end_date = reportEnd.format("YYYY-MM-DD");
    }
  }

  return reports
    .filter((report) => !isMergeableLeaveReport(report))
    .concat(mergedLeaveReports)
    .sort((first, second) => moment(first.start_date).diff(moment(second.start_date)));
};

export const groupAbsenceReportsByUser = (
  reports: Array<ReportInterface>
): Map<number, Array<ReportInterface>> => {
  const reportsByUser = new Map<number, Array<ReportInterface>>();

  for (const report of reports) {
    const userId = report.user?.id;
    if (!userId) continue;

    const userReports = reportsByUser.get(userId) || [];
    userReports.push(report);
    reportsByUser.set(userId, userReports);
  }

  reportsByUser.forEach((userReports, userId) => {
    reportsByUser.set(userId, mergeConsecutiveLeaveReports(userReports));
  });

  return reportsByUser;
};

export const getPublicHolidayDatesForWeek = (
  publicHolidays: Array<PublicHolidayRangeInterface>,
  week: Moment
): Array<Moment> => {
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = weekStart
    .clone()
    .add(WORKDAYS_PER_WEEK - 1, "days")
    .endOf("day");
  const holidayDates = new Map<string, Moment>();

  for (const publicHoliday of publicHolidays) {
    const start = moment(publicHoliday.date).startOf("day");
    if (!start.isValid()) continue;

    const duration = Math.max(publicHoliday.duration || 1, 1);
    for (let dayOffset = 0; dayOffset < duration; dayOffset += 1) {
      const date = start.clone().add(dayOffset, "days");
      if (
        date.isBefore(weekStart) ||
        date.isAfter(weekEnd) ||
        date.isoWeekday() > WORKDAYS_PER_WEEK
      ) {
        continue;
      }
      holidayDates.set(date.format("YYYY-MM-DD"), date);
    }
  }

  return [...holidayDates.values()];
};

export const getPublicHolidayWeekStyle = (
  publicHolidayDate: Moment,
  week: Moment
): Record<string, string> => {
  const dayOffset = publicHolidayDate
    .clone()
    .startOf("day")
    .diff(week.clone().startOf("isoWeek"), "days");

  return {
    left: `${dayOffset * WORKDAY_WIDTH_PERCENT}%`,
    width: `${WORKDAY_WIDTH_PERCENT}%`,
  };
};
